import type { Ergebnis, GespeichertesErgebnis } from '../shared/api.js';
import type { Modus, PruefpunktKey, Status } from '../shared/pruefpunkte.js';
import { api, ApiFehler, modus } from './api.js';
import { alsAngezeigt, db, protokolliere, type AngezeigtesErgebnis, type AusgangEintrag } from './speicher.js';

/**
 * Ergebnisse entstehen zuerst auf dem Gerät und gehen über einen Ausgang an den
 * Server. Ohne Netz bleiben sie im Ausgang und werden später gesendet.
 */

export const ereignisse = new EventTarget();
const aktuell = new Map<PruefpunktKey, AngezeigtesErgebnis>();

function meldeAenderung(): void {
  ereignisse.dispatchEvent(new Event('geaendert'));
}

export async function ladeErgebnisse(): Promise<void> {
  for (const e of await db.ergebnisse.toArray()) aktuell.set(e.pruefpunkt, e);
}

export function ergebnisVon(key: PruefpunktKey): AngezeigtesErgebnis | undefined {
  return aktuell.get(key);
}

export async function melde(pruefpunkt: PruefpunktKey, status: Status, text: string, details?: Record<string, unknown>): Promise<AngezeigtesErgebnis> {
  const eintrag: AusgangEintrag = {
    id: crypto.randomUUID(),
    pruefpunkt,
    status,
    text: text.slice(0, 500),
    ...(details ? { details } : {}),
    erfasstAm: new Date().toISOString(),
    modus: modus(),
  };
  const angezeigt: AngezeigtesErgebnis = { ...eintrag, quelle: 'geraet', gesendet: false };
  await db.transaction('rw', db.ausgang, db.ergebnisse, async () => {
    await db.ausgang.put(eintrag);
    await db.ergebnisse.put(angezeigt);
  });
  aktuell.set(pruefpunkt, angezeigt);
  meldeAenderung();
  void sendeAusgang();
  return angezeigt;
}

/** Ergebnisse, die der Server festgestellt hat (z. B. Passkey, Push-Empfang, QR-Scan). */
export async function uebernimmVomServer(liste: readonly (GespeichertesErgebnis | (Ergebnis & { modus?: Modus }))[]): Promise<void> {
  let geaendert = false;
  for (const s of liste) {
    const angezeigt =
      'quelle' in s ? alsAngezeigt(s) : { ...s, modus: s.modus ?? modus(), quelle: 'server' as const, gesendet: true };
    const lokal = aktuell.get(angezeigt.pruefpunkt);
    if (lokal && lokal.id !== angezeigt.id && Date.parse(lokal.erfasstAm) > Date.parse(angezeigt.erfasstAm)) continue;
    if (lokal && lokal.id === angezeigt.id && lokal.gesendet) continue;
    await db.ergebnisse.put(angezeigt);
    aktuell.set(angezeigt.pruefpunkt, angezeigt);
    geaendert = true;
  }
  if (geaendert) meldeAenderung();
}

let laufenderVersand: Promise<void> | undefined;
let anmeldungUngueltig = false;

/** Lehnt der Server das Token ab (Gerät anderswo weitergeführt), muss das Gerät neu eingerichtet werden. */
export function anmeldungAbgelehnt(): boolean {
  return anmeldungUngueltig;
}

/** Sendet alles aus dem Ausgang; bricht ohne Netz still ab und versucht es später erneut. */
export function sendeAusgang(): Promise<void> {
  laufenderVersand ??= versende().finally(() => {
    laufenderVersand = undefined;
    meldeAenderung();
  });
  return laufenderVersand;
}

async function versende(): Promise<void> {
  const eintraege = (await db.ausgang.toArray()).sort((a, b) => a.erfasstAm.localeCompare(b.erfasstAm));
  const nachModus = new Map<Modus, AusgangEintrag[]>();
  for (const e of eintraege) nachModus.set(e.modus, [...(nachModus.get(e.modus) ?? []), e]);

  for (const [m, liste] of nachModus) {
    for (let i = 0; i < liste.length; i += 50) {
      const stueck = liste.slice(i, i + 50);
      try {
        await api('/api/ergebnisse', { json: { ergebnisse: stueck.map(({ modus: _m, ...e }) => e) }, modus: m });
      } catch (fehler) {
        if (fehler instanceof ApiFehler && fehler.status === 400) {
          // Ungültig: entfernen, damit ein fehlerhafter Eintrag nie den Rest blockiert.
          await protokolliere('ergebnis-abgelehnt', { fehler: fehler.message, ids: stueck.map((e) => e.id) });
        } else {
          if (fehler instanceof ApiFehler && fehler.status === 401) anmeldungUngueltig = true;
          return;
        }
      }
      anmeldungUngueltig = false;
      const ids = stueck.map((e) => e.id);
      await db.transaction('rw', db.ausgang, db.ergebnisse, async () => {
        await db.ausgang.bulkDelete(ids);
        await db.ergebnisse
          .where('pruefpunkt')
          .anyOf(stueck.map((e) => e.pruefpunkt))
          .modify((e) => {
            if (ids.includes(e.id)) e.gesendet = true;
          });
      });
      for (const e of stueck) {
        const a = aktuell.get(e.pruefpunkt);
        if (a?.id === e.id) a.gesendet = true;
      }
    }
  }
}

export async function wartendeErgebnisse(): Promise<number> {
  return db.ausgang.count();
}
